import { useTranslation } from "react-i18next";

import type { CcEvidenceEvent } from "@/api/code-companion";
import { useCcEvidence } from "@/hooks/useCodeCompanion";
import { CC_HAIRLINE, CC_MONO_MUTED, CC_TABLE_HEAD_BG } from "@/lib/cc-trace-tokens";

/**
 * Os últimos eventos do ledger, correndo ao lado da matriz (S-064 onda 7).
 *
 * A matriz mostra o **estado**; o tail mostra o **acontecendo**. São perguntas
 * diferentes: uma responde "onde o pacote está", a outra "algo se moveu agora?". Sem o
 * tail, um run em progresso e um run travado têm a mesma aparência.
 *
 * **O ator veio da onda 2.** O protótipo já previa a coluna e o dado não existia: até
 * então nada dizia se a linha foi escrita pelo run, pelo terminal do dev ou por uma
 * pessoa.
 *
 * ⚠️ **Não é stream.** O protótipo desenha um ponto verde pulsando, sugerindo push. Aqui
 * é polling: o ponto só pulsa quando a consulta está de fato buscando, para não prometer
 * tempo real que não existe. SSE com cursor está no anexo de lacunas do design e segue
 * aberto.
 */

/** A hora do evento, curta. O tail é uma coluna estreita e a data completa não cabe. */
export function shortTime(iso: string | null): string {
  if (!iso) return "--:--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** O evento registrou uma falha? É o payload que diz, não o tipo. */
export function falhou(e: CcEvidenceEvent): boolean {
  return (e.payload as { ok?: unknown } | null)?.ok === false;
}

/** Cor por desfecho: só o ruim se destaca, o resto é leitura corrida. */
export function eventTone(e: CcEvidenceEvent): string {
  const ok = (e.payload as { ok?: unknown } | null)?.ok;
  if (ok === false) return "#b02a31";
  if (e.event_type === "ready_for_deploy") return "#2b8f57";
  return "var(--exed-ink-500)";
}

export default function LedgerTail({
  projectId,
  packageId,
  limit = 8,
  onOpenFull,
}: {
  projectId: string;
  packageId: number;
  limit?: number;
  onOpenFull?: () => void;
}) {
  const { t } = useTranslation();
  const { data, isFetching } = useCcEvidence(projectId, packageId);

  const eventos = (data?.events ?? []).slice(0, limit);

  return (
    <div
      data-testid="ledger-tail"
      className="flex flex-col overflow-hidden rounded-xl bg-card"
      style={{
        border: "1px solid var(--exed-card-border)",
        boxShadow: "0 1px 2px rgba(8,37,76,.04)",
      }}
    >
      <div
        className="flex items-center gap-2.5 px-4 py-3"
        style={{ borderBottom: "1px solid var(--exed-ink-50)" }}
      >
        {/* Pulsa só enquanto busca de verdade: ponto verde permanente prometeria push. */}
        <span
          data-testid={isFetching ? "tail-live" : "tail-idle"}
          style={{
            width: 7,
            height: 7,
            borderRadius: 999,
            background: isFetching ? "var(--exed-success)" : CC_HAIRLINE,
            boxShadow: isFetching ? "0 0 7px var(--exed-success)" : undefined,
          }}
        />
        <span style={{ fontFamily: "var(--font-brand)", fontWeight: 700, fontSize: "14.5px" }}>
          {t("Ledger tail")}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            color: "var(--exed-ink-400)",
            letterSpacing: ".08em",
          }}
        >
          cc_evidence_ledger
        </span>
        <div className="flex-1" />
        {/* O total do ledger, não o que cabe aqui: sem ele oito eventos parecem tudo. */}
        {data && (
          <span
            data-testid="tail-total"
            className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1"
            style={{
              background: "var(--exed-gold)",
              color: "var(--exed-navy)",
              fontFamily: "var(--font-mono)",
              fontSize: "9px",
              fontWeight: 700,
            }}
          >
            {data.total}
          </span>
        )}
      </div>

      <div className="flex-1">
        {eventos.length === 0 ? (
          <div className="px-4 py-4 text-[11px] text-muted-foreground">
            {t("Nothing recorded for this package yet.")}
          </div>
        ) : (
          eventos.map((e) => (
            <div
              key={e.id}
              className="flex items-baseline gap-2.5 px-4 py-2"
              style={{
                borderBottom: "1px solid var(--exed-ink-50)",
                fontFamily: "var(--font-mono)",
                fontSize: "10.5px",
              }}
            >
              <span style={{ color: CC_MONO_MUTED, flexShrink: 0 }}>
                {shortTime(e.created_at)}
              </span>
              {/* A cor sozinha não distingue falha de sucesso: o `event_type` é o mesmo
                  texto nos dois casos. O prefixo carrega o desfecho sem depender de ver. */}
              <span
                title={falhou(e) ? t("failed") : undefined}
                style={{ color: eventTone(e), fontWeight: 600, flexShrink: 0 }}
              >
                {falhou(e) ? "✕ " : ""}
                {e.event_type}
              </span>
              <span className="min-w-0 truncate" style={{ color: "var(--exed-ink-500)" }}>
                {e.object_name ?? ""}
              </span>
              <div className="flex-1" />
              {e.actor_type && (
                // `--exed-ink-500` e não o CC_HAIRLINE: o ator é o dado que a onda 2
                // acrescentou, e a 1.6:1 ele some. O hairline é para traço, não texto.
                <span
                  data-testid="tail-actor"
                  style={{ color: "var(--exed-ink-500)", flexShrink: 0 }}
                >
                  {e.actor_type}
                </span>
              )}
            </div>
          ))
        )}
      </div>

      {onOpenFull && (
        <button
          type="button"
          data-testid="tail-open-full"
          onClick={onOpenFull}
          className="flex items-center gap-2 px-4 py-2.5 text-left"
          style={{
            borderTop: "1px solid var(--exed-ink-50)",
            background: CC_TABLE_HEAD_BG,
            color: "var(--exed-navy)",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          {t("Open full ledger")}
        </button>
      )}
    </div>
  );
}
